import React, { useMemo, useRef } from 'react'
import { Plus, Trash2 } from 'lucide-react'
import { toast } from 'react-toastify'
import styles from './ManualProductEntrySheet.module.css'

const HEADER_LABELS = {
    productName: 'Product Name',
    brandName: 'Brand Name',
    mrp: 'MRP',
    discount: 'Discount (%)',
    priceAfterDiscount: 'Price After Discount',
    stock: 'Stock',
    minimumStockAlert: 'Minimum Stock Alert',
    vendorName: 'Vendor Name',
}

const getEmptyRow = (headers) => {
    return headers.reduce((acc, header) => {
        acc[header] = ''
        return acc
    }, {})
}

const getHeaderLabel = (header) => {
    return HEADER_LABELS[header] || header
}

const OPTIONAL_HEADERS = new Set(['brandName', 'vendorName'])

const COLUMN_CLASS_MAP = {
    productName: 'colProductName',
    brandName: 'colBrandName',
    mrp: 'colMrp',
    discount: 'colDiscount',
    priceAfterDiscount: 'colPriceAfterDiscount',
    stock: 'colStock',
    minimumStockAlert: 'colMinimumStockAlert',
    vendorName: 'colVendorName',
}

const NUMERIC_HEADERS = new Set(['mrp', 'discount', 'priceAfterDiscount', 'stock', 'minimumStockAlert'])
const MAX_NUMERIC_VALUE = 1000000000
const MAX_DECIMALS = 2

const sanitizeNumeric = (value, { min = 0, max = null, maxDecimals = MAX_DECIMALS } = {}) => {
    const original = String(value ?? '')
    const makeResult = (nextValue, { cappedToMax = false, decimalsTrimmed = false, removedInvalidChars = false } = {}) => ({
        value: nextValue,
        cappedToMax,
        decimalsTrimmed,
        removedInvalidChars,
        typedValue: original,
    })

    const stripped = String(value ?? '').replace(/[^\d.]/g, '')
    if (!stripped) return makeResult('', { removedInvalidChars: original.trim() !== '' })

    const firstDotIndex = stripped.indexOf('.')
    let normalized = stripped

    if (firstDotIndex !== -1) {
        normalized =
            stripped.slice(0, firstDotIndex + 1) +
            stripped.slice(firstDotIndex + 1).replace(/\./g, '')
    }

    if (normalized.startsWith('.')) {
        normalized = `0${normalized}`
    }

    if (!/^\d*\.?\d*$/.test(normalized)) {
        return makeResult('', { removedInvalidChars: true })
    }

    const [integerPartRaw, decimalPartRaw = ''] = normalized.split('.')
    const decimalPart = decimalPartRaw.slice(0, maxDecimals)
    const decimalsTrimmed = decimalPartRaw.length > maxDecimals
    const hasDot = normalized.includes('.')
    normalized = hasDot ? `${integerPartRaw}.${decimalPart}` : integerPartRaw

    if (normalized.endsWith('.')) {
        const integerPart = normalized.slice(0, -1)
        if (!integerPart) return makeResult('0.', { decimalsTrimmed, removedInvalidChars: stripped !== original })

        const parsedIntegerPart = Number(integerPart)
        if (!Number.isFinite(parsedIntegerPart)) return makeResult('', { decimalsTrimmed, removedInvalidChars: true })
        if (typeof max === 'number' && parsedIntegerPart > max) {
            return makeResult(String(max), { cappedToMax: true, decimalsTrimmed, removedInvalidChars: stripped !== original })
        }
        if (typeof min === 'number' && parsedIntegerPart < min) {
            return makeResult(`${min}.`, { decimalsTrimmed, removedInvalidChars: stripped !== original })
        }

        return makeResult(normalized, { decimalsTrimmed, removedInvalidChars: stripped !== original })
    }

    const parsed = Number(normalized)
    if (!Number.isFinite(parsed)) return makeResult('', { decimalsTrimmed, removedInvalidChars: true })

    if (typeof max === 'number' && parsed > max) {
        return makeResult(String(max), { cappedToMax: true, decimalsTrimmed, removedInvalidChars: stripped !== original })
    }
    if (typeof min === 'number' && parsed < min) {
        return makeResult(String(min), { decimalsTrimmed, removedInvalidChars: stripped !== original })
    }

    return makeResult(normalized, { decimalsTrimmed, removedInvalidChars: stripped !== original })
}

const ManualProductEntrySheet = ({
    headers,
    rows,
    onRowsChange,
    onValidatePreview,
    previewReady = false,
    maxRows = 25000,
}) => {
    const inputRefs = useRef({})
    const toastRef = useRef({ key: '', time: 0 })

    const safeRows = useMemo(() => {
        if (Array.isArray(rows) && rows.length > 0) {
            return rows
        }
        return [getEmptyRow(headers)]
    }, [rows, headers])

    const tableHeightPx = useMemo(() => {
        const minHeight = 320
        const maxHeight = 620
        const estimated = 84 + safeRows.length * 39
        return Math.min(maxHeight, Math.max(minHeight, estimated))
    }, [safeRows.length])

    const focusCell = (rowIndex, colIndex) => {
        const nextRef = inputRefs.current[`${rowIndex}-${colIndex}`]
        if (nextRef) {
            nextRef.focus()
            nextRef.select()
        }
    }

    const normalizeCellValue = (header, value) => {
        if (!NUMERIC_HEADERS.has(header)) {
            return { value, cappedToMax: false, decimalsTrimmed: false, removedInvalidChars: false, typedValue: String(value ?? '') }
        }

        if (header === 'discount') {
            return sanitizeNumeric(value, { min: 0, max: 100, maxDecimals: MAX_DECIMALS })
        }

        return sanitizeNumeric(value, { min: 0, max: MAX_NUMERIC_VALUE, maxDecimals: MAX_DECIMALS })
    }

    const notifyConstraint = (header, info) => {
        const now = Date.now()
        const label = getHeaderLabel(header)
        const typedValue = String(info?.typedValue ?? '').trim() || 'the entered value'

        if (info.cappedToMax) {
            const maxAllowed = header === 'discount' ? '100' : '1000000000'
            const key = `max-${header}-${typedValue}`
            if (toastRef.current.key !== key || now - toastRef.current.time > 1200) {
                toast.warning(`You typed ${typedValue} for ${label}. Max allowed value is ${maxAllowed}, resetting to ${maxAllowed}.`, {
                    position: 'top-center',
                })
                toastRef.current = { key, time: now }
            }
            return
        }

        if (info.decimalsTrimmed) {
            const key = `decimals-${header}-${typedValue}`
            if (toastRef.current.key !== key || now - toastRef.current.time > 1200) {
                toast.info(`You typed ${typedValue} for ${label}. Only 2 digits after decimal are allowed.`, {
                    position: 'top-center',
                })
                toastRef.current = { key, time: now }
            }
        }
    }

    const updateCell = (rowIndex, header, value) => {
        const normalizedInfo = normalizeCellValue(header, value)
        notifyConstraint(header, normalizedInfo)

        const nextRows = safeRows.map((row, idx) => {
            if (idx !== rowIndex) return row
            return {
                ...row,
                [header]: normalizedInfo.value,
            }
        })
        onRowsChange(nextRows)
    }

    const addRows = (count = 1) => {
        const remaining = Math.max(0, maxRows - safeRows.length)
        if (remaining === 0) return
        const batchSize = Math.min(count, remaining)
        const appended = Array.from({ length: batchSize }, () => getEmptyRow(headers))
        onRowsChange([...safeRows, ...appended])
    }

    const deleteRow = (rowIndex) => {
        if (safeRows.length <= 1) {
            onRowsChange([getEmptyRow(headers)])
            return
        }
        onRowsChange(safeRows.filter((_, idx) => idx !== rowIndex))
    }

    const clearSheet = () => {
        onRowsChange([getEmptyRow(headers)])
    }

    const handleKeyboardNavigation = (event, rowIndex, colIndex) => {
        const isShiftTab = event.key === 'Tab' && event.shiftKey
        const isTab = event.key === 'Tab' && !event.shiftKey
        const isEnter = event.key === 'Enter'
        const isArrowUp = event.key === 'ArrowUp'
        const isArrowDown = event.key === 'ArrowDown'
        const isArrowLeft = event.key === 'ArrowLeft'
        const isArrowRight = event.key === 'ArrowRight'

        if (!isTab && !isShiftTab && !isEnter && !isArrowUp && !isArrowDown && !isArrowLeft && !isArrowRight) {
            return
        }

        event.preventDefault()

        const lastCol = headers.length - 1
        const lastRow = safeRows.length - 1
        let nextRow = rowIndex
        let nextCol = colIndex

        if (isEnter || isArrowDown) {
            nextRow = rowIndex + 1
        } else if (isArrowUp) {
            nextRow = Math.max(0, rowIndex - 1)
        } else if (isTab) {
            if (colIndex === lastCol) {
                nextCol = 0
                nextRow = rowIndex + 1
            } else {
                nextCol = colIndex + 1
            }
        } else if (isShiftTab) {
            if (colIndex === 0) {
                nextCol = lastCol
                nextRow = Math.max(0, rowIndex - 1)
            } else {
                nextCol = colIndex - 1
            }
        } else if (isArrowLeft) {
            nextCol = Math.max(0, colIndex - 1)
        } else if (isArrowRight) {
            nextCol = Math.min(lastCol, colIndex + 1)
        }

        if (nextRow > lastRow) {
            if (safeRows.length < maxRows) {
                const nextRows = [...safeRows, getEmptyRow(headers)]
                onRowsChange(nextRows)
                setTimeout(() => focusCell(nextRow, nextCol), 0)
            }
            return
        }

        focusCell(nextRow, nextCol)
    }

    const handlePaste = (event, startRow, startCol) => {
        const pastedText = event.clipboardData?.getData('text') || ''
        if (!pastedText.includes('\n') && !pastedText.includes('\t')) return

        event.preventDefault()

        const rowChunks = pastedText
            .replace(/\r/g, '')
            .split('\n')
            .filter((line) => line.length > 0)

        if (rowChunks.length === 0) return

        const nextRows = [...safeRows]

        rowChunks.forEach((line, rowOffset) => {
            const targetRow = startRow + rowOffset
            if (targetRow >= maxRows) return

            while (targetRow >= nextRows.length) {
                nextRows.push(getEmptyRow(headers))
            }

            const values = line.split('\t')
            values.forEach((value, colOffset) => {
                const targetCol = startCol + colOffset
                if (targetCol >= headers.length) return
                const header = headers[targetCol]
                const normalizedInfo = normalizeCellValue(header, value)
                notifyConstraint(header, normalizedInfo)
                nextRows[targetRow] = {
                    ...nextRows[targetRow],
                    [header]: normalizedInfo.value,
                }
            })
        })

        onRowsChange(nextRows)
    }

    return (
        <div className={styles.sheetWrapper}>
            <div className={styles.sheetHeader}>
                <div>
                    <h3>Manual Entry Sheet</h3>
                    <p>Type directly, use tab/enter/arrows to move, or paste from Excel/Google Sheets. Keep entering data and click Validate & Preview when ready.</p>
                </div>
                <div className={styles.sheetActions}>
                    <button className={styles.actionBtn} onClick={() => addRows(1)}>
                        <Plus size={14} /> Add Row
                    </button>
                    <button className={styles.clearBtn} onClick={clearSheet}>
                        Clear Sheet
                    </button>
                    <button className={styles.validateBtn} onClick={onValidatePreview}>
                        {previewReady ? 'Re-Validate & Preview' : 'Validate & Preview'}
                    </button>
                </div>
            </div>

            <div className={styles.sheetTableWrap} style={{ height: `${tableHeightPx}px` }}>
                <table className={styles.sheetTable}>
                    <thead>
                        <tr>
                            <th className={styles.rowNumber}>#</th>
                            {headers.map((header) => (
                                <th key={header} className={styles[COLUMN_CLASS_MAP[header]]}>
                                    <span className={styles.headerLabel}>
                                        {getHeaderLabel(header)}
                                        {OPTIONAL_HEADERS.has(header) && <span className={styles.optionalText}>(optional)</span>}
                                    </span>
                                </th>
                            ))}
                            <th className={styles.rowAction}>Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {safeRows.map((row, rowIndex) => (
                            <tr key={`manual-row-${rowIndex}`}>
                                <td className={styles.rowNumber}>{rowIndex + 1}</td>
                                {headers.map((header, colIndex) => (
                                    <td key={`${header}-${rowIndex}`} className={styles[COLUMN_CLASS_MAP[header]]}>
                                        <input
                                            ref={(node) => {
                                                inputRefs.current[`${rowIndex}-${colIndex}`] = node
                                            }}
                                            value={row?.[header] ?? ''}
                                            onChange={(event) => updateCell(rowIndex, header, event.target.value)}
                                            onKeyDown={(event) => handleKeyboardNavigation(event, rowIndex, colIndex)}
                                            onPaste={(event) => handlePaste(event, rowIndex, colIndex)}
                                            placeholder={getHeaderLabel(header)}
                                            inputMode={NUMERIC_HEADERS.has(header) ? 'decimal' : 'text'}
                                        />
                                    </td>
                                ))}
                                <td className={styles.rowAction}>
                                    <button
                                        className={styles.deleteRowBtn}
                                        onClick={() => deleteRow(rowIndex)}
                                        title="Delete row"
                                    >
                                        <Trash2 size={13} />
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <div className={styles.sheetFooter}>
                {safeRows.length} row(s) ready · max {maxRows}
            </div>
        </div>
    )
}

export default ManualProductEntrySheet
